import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Flag, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/business/PageHeader";
import { resetFeatureFlagCache } from "@/hooks/useFeatureFlag";

export default function AdminFeatureFlags() {
  const [flags, setFlags] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/flags");
    setFlags(data || []);
  };

  useEffect(() => { load(); }, []);

  const toggle = async (key) => {
    try {
      await api.post(`/admin/flags/${key}/toggle`);
      resetFeatureFlagCache();
      toast.success(`Flag "${key}" toggled`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };

  const save = async (patch) => {
    try {
      await api.put(`/admin/flags/${editing.key}`, patch);
      resetFeatureFlagCache();
      toast.success("Flag updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto">
        <PageHeader className="mb-2">
          <PageHeader.Title>Feature Flags</PageHeader.Title>
        </PageHeader>
        <p className="mb-6 text-sm text-muted-foreground">
          Global on/off, per-firm &amp; per-user targeting, percentage rollout, dependencies, scheduled windows. Every change is audit-logged.
        </p>

        <div className="grid gap-3">
          {flags.map((f) => (
            <Card key={f.key} className="p-4 border-border" data-testid={`flag-row-${f.key}`}>
              <div className="flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-base font-semibold">{f.name || f.key}</span>
                    <code className="text-xs px-1.5 py-0.5 rounded bg-slate-100">{f.key}</code>
                    {f.owner && <Badge variant="outline" className="text-xs">owner: {f.owner}</Badge>}
                    {(f.depends_on || []).length > 0 && (
                      <Badge variant="outline" className="text-xs">
                        depends: {f.depends_on.join(", ")}
                      </Badge>
                    )}
                  </div>
                  {f.description && <div className="mt-1 text-xs text-muted-foreground">{f.description}</div>}
                  <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                    <span>rollout: <b>{f.rollout_percentage ?? 0}%</b></span>
                    <span>firms: <b>{(f.target_firm_ids || []).length}</b></span>
                    <span>users: <b>{(f.target_user_ids || []).length}</b></span>
                    {f.start_at && <span>starts: {new Date(f.start_at).toLocaleDateString()}</span>}
                    {f.end_at && <span>ends: {new Date(f.end_at).toLocaleDateString()}</span>}
                    {f.expiry_date && <span>expires: {new Date(f.expiry_date).toLocaleDateString()}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={!!f.enabled}
                      onCheckedChange={() => toggle(f.key)}
                      data-testid={`flag-toggle-${f.key}`}
                    />
                    <span className="text-xs">{f.enabled ? "on" : "off"}</span>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setEditing(f)} data-testid={`flag-edit-${f.key}`}>
                    Edit
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {editing && (
          <FlagEditor
            flag={editing}
            allFlags={flags}
            onClose={() => setEditing(null)}
            onSave={save}
          />
        )}
      </div>
    </AppShell>
  );
}

function FlagEditor({ flag, allFlags, onClose, onSave }) {
  const [form, setForm] = useState({
    name: flag.name || "",
    description: flag.description || "",
    owner: flag.owner || "",
    enabled: !!flag.enabled,
    rollout_percentage: flag.rollout_percentage ?? 0,
    target_firm_ids: (flag.target_firm_ids || []).join(","),
    target_user_ids: (flag.target_user_ids || []).join(","),
    depends_on: (flag.depends_on || []).join(","),
    start_at: flag.start_at || "",
    end_at: flag.end_at || "",
    expiry_date: flag.expiry_date || "",
    rollback_plan: flag.rollback_plan || "",
    envs: {
      preview:    flag.environments?.preview !== false,
      staging:    flag.environments?.staging !== false,
      production: flag.environments?.production !== false,
    },
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const parseList = (s) => (s || "").split(",").map(x => x.trim()).filter(Boolean);

  const submit = () => {
    onSave({
      name: form.name || null,
      description: form.description || null,
      owner: form.owner || null,
      enabled: form.enabled,
      rollout_percentage: Number(form.rollout_percentage),
      target_firm_ids: parseList(form.target_firm_ids),
      target_user_ids: parseList(form.target_user_ids),
      depends_on: parseList(form.depends_on),
      start_at: form.start_at || null,
      end_at: form.end_at || null,
      expiry_date: form.expiry_date || null,
      rollback_plan: form.rollback_plan || null,
      environments: form.envs,
    });
  };

  const otherFlags = allFlags.filter(f => f.key !== flag.key).map(f => f.key);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Edit flag · {flag.key}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={form.name} onChange={set("name")} data-testid="flag-form-name" /></div>
            <div><Label>Owner</Label><Input value={form.owner} onChange={set("owner")} data-testid="flag-form-owner" /></div>
          </div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={set("description")} rows={2} /></div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-3 pt-6">
              <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="flag-form-enabled" />
              <Label>Globally enabled</Label>
            </div>
            <div>
              <Label>Rollout %</Label>
              <Input type="number" min={0} max={100} value={form.rollout_percentage} onChange={set("rollout_percentage")} data-testid="flag-form-rollout" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Target firm ids (comma-sep)</Label><Input value={form.target_firm_ids} onChange={set("target_firm_ids")} /></div>
            <div><Label>Target user ids (comma-sep)</Label><Input value={form.target_user_ids} onChange={set("target_user_ids")} /></div>
          </div>

          <div>
            <Label>Depends on (comma-sep flag keys)</Label>
            <Input value={form.depends_on} onChange={set("depends_on")} placeholder={otherFlags.join(", ")} data-testid="flag-form-depends" />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div><Label>Start at (ISO)</Label><Input value={form.start_at} onChange={set("start_at")} placeholder="2026-03-01T00:00:00Z" /></div>
            <div><Label>End at (ISO)</Label><Input value={form.end_at} onChange={set("end_at")} /></div>
            <div><Label>Expiry (ISO)</Label><Input value={form.expiry_date} onChange={set("expiry_date")} /></div>
          </div>

          <div>
            <Label>Environments</Label>
            <div className="flex flex-wrap gap-4 mt-2">
              {["preview", "staging", "production"].map((env) => (
                <label key={env} className="flex items-center gap-2 text-sm">
                  <Switch
                    checked={form.envs[env]}
                    onCheckedChange={(v) => setForm({ ...form, envs: { ...form.envs, [env]: v } })}
                  />
                  {env}
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label>Rollback plan</Label>
            <Textarea value={form.rollback_plan} onChange={set("rollback_plan")} rows={2} />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}><RotateCcw className="h-4 w-4 mr-1" />Cancel</Button>
          <Button onClick={submit} data-testid="flag-form-save"><Save className="h-4 w-4 mr-1" />Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
